import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, Clock, Calendar, Coffee, Utensils, Users, BookOpen, Menu, X } from 'lucide-react';

const Schedule = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    pl: {
      title: 'Harmonogram Kursu',
      subtitle: 'Szczegółowy plan zajęć i sesji',
      dailySchedule: {
        title: 'Harmonogram Dnia (Poniedziałek – Piątek)',
        sessions: [
          { time: '08:30 - 10:00', activity: 'Sesja 1', duration: '90 min', type: 'session' },
          { time: '10:00 - 10:15', activity: 'Przerwa kawowa', duration: '15 min', type: 'break' },
          { time: '10:15 - 11:45', activity: 'Sesja 2', duration: '90 min', type: 'session' },
          { time: '11:45 - 12:00', activity: 'Krótka przerwa przed lunchem', duration: '15 min', type: 'break' },
          { time: '12:00 - 13:00', activity: 'PRZERWA NA LUNCH', duration: '60 min', type: 'lunch' },
          { time: '13:00 - 14:30', activity: 'Sesja 3', duration: '90 min', type: 'session' },
          { time: '14:30 - 14:45', activity: 'Przerwa regeneracyjna', duration: '15 min', type: 'break' },
          { time: '14:45 - 16:15', activity: 'Sesja 4', duration: '90 min', type: 'session' },
          { time: '16:15 - 17:00', activity: 'sesja pytań i odpowiedzi / Q&A', duration: '45 min', type: 'free' },
          { time: '17:00 - 19:00', activity: 'Sesje indywidualne (dla chętnych)', duration: '120 min', type: 'individual' }
        ]
      },
      courseStructure: {
        title: 'Struktura Kursów',
        subtitle: '10 sesji na każdy kurs (2 x 2,5 dnia)',
        courses: [
          {
            name: 'KURS 1',
            period: 'Poniedziałek – Środa rano',
            schedule: [
              { day: 'Poniedziałek', sessions: 'Sesje 1, 2, 3, 4', count: '4 sesje' },
              { day: 'Wtorek', sessions: 'Sesje 5, 6, 7, 8', count: '4 sesje' },
              { day: 'Środa (rano)', sessions: 'Sesje 9, 10', count: '2 sesje', note: 'Kurs kończy się o 11:45' }
            ]
          },
          {
            name: 'KURS 2',
            period: 'Środa po południu – Piątek',
            schedule: [
              { day: 'Środa (po lunchu)', sessions: 'Sesje 1, 2', count: '2 sesje', note: 'Kurs zaczyna się o 13:00' },
              { day: 'Czwartek', sessions: 'Sesje 3, 4, 5, 6', count: '4 sesje' },
              { day: 'Piątek', sessions: 'Sesje 7, 8, 9, 10', count: '4 sesje', note: 'Kurs kończy się o 16:15' }
            ]
          }
        ]
      },
      subjectSchedule: {
        title: 'Harmonogram Przedmiotowy',
        subjects: [
          { subject: 'Biology SL', dates: '2-4.02', instructor: 'dr David O\'Keeffe' },
          { subject: 'Biology HL', dates: '4-6.02', instructor: 'dr David O\'Keeffe' },
          { subject: 'Geography SL', dates: '2.02 (13:30)-4.02', instructor: 'mgr Anna Krzemińska-Kaczyńska' },
          { subject: 'Geography HL', dates: '5-6.02', instructor: 'mgr Anna Krzemińska-Kaczyńska' },
          { subject: 'Mathematics AA SL', dates: '9-11.02', instructor: 'dr Bill Roberts' },
          { subject: 'Mathematics AA HL', dates: '11-13.02', instructor: 'dr Bill Roberts' }
        ],
        individual: {
          title: 'Sesje Indywidualne',
          sessions: [
            { dates: '2-7.02 (17:00-19:00)', subjects: 'Biology, Geography, Polish Literature, English B' },
            { dates: '12-15.02 (17:00-19:00)', subjects: 'Mathematics AA, Geography, Polish Literature, English B' }
          ]
        }
      },
      weeklyOverview: {
        title: 'Szczegółowy Rozkład Tygodniowy',
        days: [
          {
            day: 'Poniedziałek',
            morning1: 'Kurs 1 - Sesja 1',
            morning2: 'Kurs 1 - Sesja 2',
            afternoon1: 'Kurs 1 - Sesja 3',
            afternoon2: 'Kurs 1 - Sesja 4'
          },
          {
            day: 'Wtorek',
            morning1: 'Kurs 1 - Sesja 5',
            morning2: 'Kurs 1 - Sesja 6',
            afternoon1: 'Kurs 1 - Sesja 7',
            afternoon2: 'Kurs 1 - Sesja 8'
          },
          {
            day: 'Środa',
            morning1: 'Kurs 1 - Sesja 9',
            morning2: 'Kurs 1 - Sesja 10',
            lunch: 'KONIEC KURS 1 - Refleksja CAS',
            afternoon1: 'START KURS 2 - Sesja 1',
            afternoon2: 'Kurs 2 - Sesja 2'
          },
          {
            day: 'Czwartek',
            morning1: 'Kurs 2 - Sesja 3',
            morning2: 'Kurs 2 - Sesja 4',
            afternoon1: 'Kurs 2 - Sesja 5',
            afternoon2: 'Kurs 2 - Sesja 6'
          },
          {
            day: 'Piątek',
            morning1: 'Kurs 2 - Sesja 7',
            morning2: 'Kurs 2 - Sesja 8',
            afternoon1: 'Kurs 2 - Sesja 9',
            afternoon2: 'Kurs 2 - Sesja 10'
          }
        ]
      }
    },
    en: {
      title: 'Course Schedule',
      subtitle: 'Detailed plan of classes and sessions',
      dailySchedule: {
        title: 'Daily Schedule (Monday – Friday)',
        sessions: [
          { time: '08:30 - 10:00', activity: 'Session 1', duration: '90 min', type: 'session' },
          { time: '10:00 - 10:15', activity: 'Coffee break', duration: '15 min', type: 'break' },
          { time: '10:15 - 11:45', activity: 'Session 2', duration: '90 min', type: 'session' },
          { time: '11:45 - 12:00', activity: 'Short break before lunch', duration: '15 min', type: 'break' },
          { time: '12:00 - 13:00', activity: 'LUNCH BREAK', duration: '60 min', type: 'lunch' },
          { time: '13:00 - 14:30', activity: 'Session 3', duration: '90 min', type: 'session' },
          { time: '14:30 - 14:45', activity: 'Regeneration break', duration: '15 min', type: 'break' },
          { time: '14:45 - 16:15', activity: 'Session 4', duration: '90 min', type: 'session' },
          { time: '16:15 - 17:00', activity: 'Q&A session / sesja pytań i odpowiedzi', duration: '45 min', type: 'free' },
          { time: '17:00 - 19:00', activity: 'Individual sessions (optional)', duration: '120 min', type: 'individual' }
        ]
      },
      courseStructure: {
        title: 'Course Structure',
        subtitle: '10 sessions per course (2 x 2.5 days)',
        courses: [
          {
            name: 'COURSE 1',
            period: 'Monday – Wednesday morning',
            schedule: [
              { day: 'Monday', sessions: 'Sessions 1, 2, 3, 4', count: '4 sessions' },
              { day: 'Tuesday', sessions: 'Sessions 5, 6, 7, 8', count: '4 sessions' },
              { day: 'Wednesday (morning)', sessions: 'Sessions 9, 10', count: '2 sessions', note: 'Course ends at 11:45' }
            ]
          },
          {
            name: 'COURSE 2',
            period: 'Wednesday afternoon – Friday',
            schedule: [
              { day: 'Wednesday (after lunch)', sessions: 'Sessions 1, 2', count: '2 sessions', note: 'Course starts at 13:00' },
              { day: 'Thursday', sessions: 'Sessions 3, 4, 5, 6', count: '4 sessions' },
              { day: 'Friday', sessions: 'Sessions 7, 8, 9, 10', count: '4 sessions', note: 'Course ends at 16:15' }
            ]
          }
        ]
      },
      subjectSchedule: {
        title: 'Subject Schedule',
        subjects: [
          { subject: 'Biology SL', dates: '2-4.02', instructor: 'Dr David O\'Keeffe' },
          { subject: 'Biology HL', dates: '4-6.02', instructor: 'Dr David O\'Keeffe' },
          { subject: 'Geography SL', dates: '2.02 (13:30)-4.02', instructor: 'Anna Krzemińska-Kaczyńska, MA' },
          { subject: 'Geography HL', dates: '5-6.02', instructor: 'Anna Krzemińska-Kaczyńska, MA' },
          { subject: 'Mathematics AA SL', dates: '9-11.02', instructor: 'Dr Bill Roberts' },
          { subject: 'Mathematics AA HL', dates: '11-13.02', instructor: 'Dr Bill Roberts' }
        ],
        individual: {
          title: 'Individual Sessions',
          sessions: [
            { dates: '2-7.02 (17:00-19:00)', subjects: 'Biology, Geography, Polish Literature, English B' },
            { dates: '12-15.02 (17:00-19:00)', subjects: 'Mathematics AA, Geography, Polish Literature, English B' }
          ]
        }
      },
      weeklyOverview: {
        title: 'Detailed Weekly Schedule',
        days: [
          {
            day: 'Monday',
            morning1: 'Course 1 - Session 1',
            morning2: 'Course 1 - Session 2',
            afternoon1: 'Course 1 - Session 3',
            afternoon2: 'Course 1 - Session 4'
          },
          {
            day: 'Tuesday',
            morning1: 'Course 1 - Session 5',
            morning2: 'Course 1 - Session 6',
            afternoon1: 'Course 1 - Session 7',
            afternoon2: 'Course 1 - Session 8'
          },
          {
            day: 'Wednesday',
            morning1: 'Course 1 - Session 9',
            morning2: 'Course 1 - Session 10',
            lunch: 'END COURSE 1 - CAS Reflection',
            afternoon1: 'START COURSE 2 - Session 1',
            afternoon2: 'Course 2 - Session 2'
          },
          {
            day: 'Thursday',
            morning1: 'Course 2 - Session 3',
            morning2: 'Course 2 - Session 4',
            afternoon1: 'Course 2 - Session 5',
            afternoon2: 'Course 2 - Session 6'
          },
          {
            day: 'Friday',
            morning1: 'Course 2 - Session 7',
            morning2: 'Course 2 - Session 8',
            afternoon1: 'Course 2 - Session 9',
            afternoon2: 'Course 2 - Session 10'
          }
        ]
      }
    }
  };

  const t = content[language];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'session': return <BookOpen className="w-4 h-4" />;
      case 'break': return <Coffee className="w-4 h-4" />;
      case 'lunch': return <Utensils className="w-4 h-4" />;
      case 'individual': return <Users className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'session': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'break': return 'bg-green-100 text-green-800 border-green-200';
      case 'lunch': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'individual': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'free': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">IB</span>
            </div>
            <span className="font-bold text-xl">DP Accelerator</span>
          </Link>
          
          <div className="hidden md:flex space-x-6">
            <Link to="/" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Strona główna' : 'Home'}</Link>
            <Link to="/experts" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Eksperci' : 'Experts'}</Link>
            <Link to="/schedule" className="text-blue-600 font-semibold">{language === 'pl' ? 'Harmonogram' : 'Schedule'}</Link>
            <Link to="/course-topics" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Tematy' : 'Topics'}</Link>
            <Link to="/pricing" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}</Link>
            <Link to="/code-of-conduct" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Regulamin' : 'Code of Conduct'}</Link>
            <Link to="/contact" className="hover:text-blue-600 transition-colors">{language === 'pl' ? 'Kontakt' : 'Contact'}</Link>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
              className="flex items-center space-x-1"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'pl' ? 'EN' : 'PL'}</span>
            </Button>
            
            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-b shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              <Link 
                to="/" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Strona główna' : 'Home'}
              </Link>
              <Link 
                to="/experts" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Eksperci' : 'Experts'}
              </Link>
              <Link 
                to="/schedule" 
                className="block text-blue-600 font-semibold"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Harmonogram' : 'Schedule'}
              </Link>
              <Link 
                to="/course-topics" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Tematy' : 'Topics'}
              </Link>
              <Link 
                to="/pricing" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
              </Link>
              <Link 
                to="/code-of-conduct" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
              </Link>
              <Link 
                to="/contact" 
                className="block hover:text-blue-600 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {language === 'pl' ? 'Kontakt' : 'Contact'}
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-yellow-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Subject Schedule */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">{t.subjectSchedule.title}</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {t.subjectSchedule.subjects.map((subject, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-xl">{subject.subject}</CardTitle>
                  <CardDescription className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {subject.dates}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{subject.instructor}</p>
                  <div className="mt-4 text-xs text-gray-400 font-medium">
                    #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">{t.subjectSchedule.individual.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {t.subjectSchedule.individual.sessions.map((session, index) => (
                  <div key={index} className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center mb-2 md:mb-0">
                      <Clock className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="font-semibold">{session.dates}</span>
                    </div>
                    <div className="text-gray-600">
                      {session.subjects}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-xs text-gray-400 font-medium text-center">
                #EducationExcellence #QualityTourism #TimeLeverage #ValuesDrivenEducation #TrustInTeaching #SustainablePerformance
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Daily Schedule */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">{t.dailySchedule.title}</h2>
          
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-0">
                <div className="space-y-0">
                  {t.dailySchedule.sessions.map((session, index) => (
                    <div key={index} className={`flex items-center p-4 border-b last:border-b-0 ${getActivityColor(session.type)}`}>
                      <div className="flex items-center space-x-3 flex-1">
                        {getActivityIcon(session.type)}
                        <div className="flex-1">
                          <div className="font-semibold">{session.time}</div>
                          <div className="text-sm opacity-90">{session.activity}</div>
                        </div>
                        <Badge variant="outline" className="bg-white/50">
                          {session.duration}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Course Structure */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">{t.courseStructure.title}</h2>
            <p className="text-xl text-gray-600">{t.courseStructure.subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {t.courseStructure.courses.map((course, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl text-blue-600">{course.name}</CardTitle>
                  <CardDescription className="text-lg">{course.period}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {course.schedule.map((day, dayIndex) => (
                      <div key={dayIndex} className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-semibold text-gray-900 mb-2">{day.day}</div>
                        <div className="text-gray-600 mb-1">{day.sessions}</div>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline">{day.count}</Badge>
                          {day.note && (
                            <span className="text-sm text-blue-600 italic">{day.note}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Weekly Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">{t.weeklyOverview.title}</h2>
          
          <div className="overflow-x-auto">
            <div className="min-w-full">
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-4 text-left font-semibold">{language === 'pl' ? 'Dzień' : 'Day'}</th>
                        <th className="p-4 text-center font-semibold">08:30-10:00</th>
                        <th className="p-4 text-center font-semibold">10:15-11:45</th>
                        <th className="p-4 text-center font-semibold">12:00-13:00</th>
                        <th className="p-4 text-center font-semibold">13:00-14:30</th>
                        <th className="p-4 text-center font-semibold">14:45-16:15</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.weeklyOverview.days.map((day, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-4 font-semibold bg-blue-50">{day.day}</td>
                          <td className="p-4 text-center text-sm bg-blue-100">{day.morning1}</td>
                          <td className="p-4 text-center text-sm bg-blue-100">{day.morning2}</td>
                          <td className="p-4 text-center text-sm bg-orange-100">
                            {day.lunch || (language === 'pl' ? 'PRZERWA' : 'BREAK')}
                          </td>
                          <td className="p-4 text-center text-sm bg-green-100">{day.afternoon1}</td>
                          <td className="p-4 text-center text-sm bg-green-100">{day.afternoon2}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Key Information */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-900">
                  {language === 'pl' ? 'Kluczowe Informacje' : 'Key Information'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-3">
                      {language === 'pl' ? 'Struktura kursu:' : 'Course structure:'}
                    </h4>
                    <ul className="space-y-2 text-blue-800">
                      <li>• {language === 'pl' ? '20 sesji łącznie (10 na kurs)' : '20 sessions total (10 per course)'}</li>
                      <li>• {language === 'pl' ? '90 minut każda sesja' : '90 minutes per session'}</li>
                      <li>• {language === 'pl' ? '4 sesje dziennie' : '4 sessions per day'}</li>
                      <li>• {language === 'pl' ? 'Maksymalnie 10 osób w grupie' : 'Maximum 10 people per group'}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-3">
                      {language === 'pl' ? 'Przerwy i posiłki:' : 'Breaks and meals:'}
                    </h4>
                    <ul className="space-y-2 text-blue-800">
                      <li>• {language === 'pl' ? '15-minutowe przerwy między sesjami' : '15-minute breaks between sessions'}</li>
                      <li>• {language === 'pl' ? '60-minutowa przerwa na lunch' : '60-minute lunch break'}</li>
                      <li>• {language === 'pl' ? 'Wspólne śniadania wliczone' : 'Shared breakfasts included'}</li>
                      <li>• {language === 'pl' ? 'Nielimitowana woda' : 'Unlimited water'}</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">IB</span>
              </div>
              <span className="font-bold text-xl">DP Accelerator</span>
            </div>
            <p className="text-gray-400">
              {language === 'pl' 
                ? 'Intensywne kursy przygotowawcze do matury międzynarodowej IB.' 
                : 'Intensive preparation courses for the International Baccalaureate.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Schedule;